import React, { Fragment } from 'react';
import Input from 'components/ui/input';
import InputAmount from 'components/ui/inputAmount';
import CustomSelect from 'components/ui/customSelect';
import CopyToClipboard from 'components/ui/copyToClipboard';
import { AnyObject } from 'types/Common';
import { Handlers, SelectInterface, SettlementData } from 'pages/settlement/types';
import { GetTranslate, IntlProps } from 'decorators/addTranslation';
import InputDate from 'components/ui/inputDate/InputDate';
import Date from 'helpers/Date';

type IOptions = {
  currencies: SelectInterface;
  statuses: SelectInterface;
  userMerchant: SelectInterface;
  settlementBalanceUser: SelectInterface;
}

interface IFormData extends IntlProps {
  formData: SettlementData;
  handlers: Handlers;
  validationErrors: AnyObject;
  options: IOptions;
  content: SettlementData;
}

interface SettlementFormFieldsProps extends IFormData {
  isNew: boolean;
  isLoading: boolean;
}

export const SettlementFormFields: React.FC<SettlementFormFieldsProps> = ({
  isNew,
  content,
  formData,
  handlers,
  validationErrors,
  options,
  getTranslate
}: {
  isNew: boolean,
  content: Partial<SettlementData>,
  formData: SettlementData,
  handlers: Handlers,
  validationErrors: AnyObject,
  options: IOptions,
  getTranslate: GetTranslate
}) => {
  if (isNew) {
    return <NewSettlementFields
      content={content}
      formData={formData}
      handlers={handlers}
      validationErrors={validationErrors}
      options={options}
      getTranslate={getTranslate}
    />;
  }

  return <EditSettlementFields
    content={content}
    formData={formData}
    handlers={handlers}
    validationErrors={validationErrors}
    options={options}
    getTranslate={getTranslate}
  />;
};

const NewSettlementFields: React.FC<IFormData> = ({
  content,
  formData,
  handlers,
  validationErrors,
  options,
  getTranslate
} : {
  content: SettlementData,
  formData: SettlementData,
  handlers: Handlers,
  validationErrors: AnyObject,
  options: IOptions,
  getTranslate: GetTranslate
}) => (
  <Fragment>
    <div className="utils-flex utils-flex-gap-1">
      <Input
        id="walletAddress"
        label={getTranslate('settlements.registry.walletAddress.header')}
        value={formData.walletAddress || ''}
        placeholder={getTranslate('settlements.registry.walletAddress.header')}
        onChange={handlers.walletAddress}
        error={validationErrors?.walletAddress}
        disabled={!!content.walletAddress}
        isRequired
        modern
      />
      {content.walletAddress && (
        <CopyToClipboard text={content.walletAddress} iconSize={18} />
      )}
    </div>

    <InputAmount
      label={getTranslate('settlements.modal.fields.amount')}
      id="input-amount"
      placeholder="0.00"
      value={formData.amount || ''}
      onChange={handlers.amount}
      modern
      error={validationErrors?.amount}
      isRequired
      fractional={6}
    />

    <CustomSelect
      id="settlement.currency"
      label={getTranslate('settlements.registry.currency.header')}
      placeholder={getTranslate('settlements.registry.currency.header')}
      value={formData.currency}
      options={options.currencies.list}
      isLoading={options.currencies.isLoading}
      onChange={handlers.currency}
      error={validationErrors.currency}
      isSearchable
      isRequired
      modern
    />

    <CustomSelect
      id="settlement.balanceId"
      label={getTranslate('settlements.registry.balance.header')}
      placeholder={getTranslate('settlements.registry.balance.header')}
      value={formData.balanceId}
      options={options.settlementBalanceUser.list}
      isLoading={options.settlementBalanceUser.isLoading}
      onChange={handlers.balanceId}
      error={validationErrors.balanceId}
      isSearchable
      isRequired={false}
      modern
    />

    <Input
      value={formData.comment || ''}
      id="settlement.comment"
      error={validationErrors?.comment}
      label={getTranslate('settlements.registry.comment.header')}
      placeholder={getTranslate('settlements.registry.comment.header')}
      type="textarea"
      resize={false}
      onChange={handlers.comment}
      modern
    />
  </Fragment>
);

const EditSettlementFields: React.FC<IFormData> = ({
   content,
   formData,
   handlers,
   validationErrors,
   options,
   getTranslate
 }: {
  content: Partial<SettlementData>,
  formData: SettlementData,
  handlers: Handlers,
  validationErrors: AnyObject,
  options: IOptions,
  getTranslate: GetTranslate
}) => (
  <Fragment>
    <Input
      id="settlement.id"
      label={getTranslate('settlements.registry.id.header')}
      value={content?.id || ''}
      placeholder={getTranslate('settlements.registry.id.header')}
      disabled
      isRequired
      modern
    />

    <CustomSelect
      id="settlement.status"
      label={getTranslate('settlements.registry.status.header')}
      placeholder={getTranslate('settlements.registry.status.header')}
      value={formData.status}
      options={options.statuses.list}
      isLoading={options.statuses.isLoading}
      onChange={handlers.status}
      error={validationErrors.status}
      isSearchable
      isRequired
      modern
    />

    <Input
      id="settlement.walletAddress"
      label={getTranslate('settlements.registry.walletAddress.header')}
      value={formData.walletAddress || ''}
      placeholder={getTranslate('settlements.registry.walletAddress.header')}
      onChange={handlers.walletAddress}
      error={validationErrors?.walletAddress}
      isRequired
      modern
    />

    <CustomSelect
      id="settlement.merchant"
      label={getTranslate('settlements.registry.merchant.header')}
      placeholder={getTranslate('settlements.registry.merchant.header')}
      value={formData.merchantId}
      options={options.userMerchant.list}
      isLoading={options.userMerchant.isLoading}
      onChange={handlers.merchantId}
      error={validationErrors.merchantId}
      isSearchable
      modern
    />

    <InputAmount
      label={getTranslate('settlements.registry.requestAmount.header')}
      id="settlements.requestAmount"
      placeholder="0.00"
      value={formData.requestAmount || ''}
      onChange={handlers.requestAmount}
      modern
      error={validationErrors?.requestAmount}
    />

    <CustomSelect
      id="settlement.currency"
      label={getTranslate('settlements.registry.currency.header')}
      placeholder={getTranslate('settlements.registry.currency.header')}
      value={formData.currency}
      options={options.currencies.list}
      isLoading={options.currencies.isLoading}
      onChange={handlers.currency}
      error={validationErrors.currency}
      isSearchable
      isRequired
      modern
    />

    <CustomSelect
      id="settlement.balanceId"
      label={getTranslate('settlements.registry.balance.header')}
      placeholder={getTranslate('settlements.registry.balance.header')}
      value={formData.balanceId}
      options={options.settlementBalanceUser.list}
      isLoading={options.settlementBalanceUser.isLoading}
      onChange={handlers.balanceId}
      error={validationErrors.balanceId}
      isSearchable
      isRequired={false}
      modern
      autoScrollToSelectedOption
    />

    <InputAmount
      label={getTranslate('settlements.registry.amount.header')}
      id="settlements.amount"
      placeholder="0.00"
      value={formData.amount || ''}
      onChange={handlers.amount}
      modern
      error={validationErrors?.amount}
      fractional={6}
    />

    <InputAmount
      label={getTranslate('settlements.registry.fee.header')}
      id="settlements.fee"
      placeholder="0.00"
      value={formData.fee || ''}
      onChange={handlers.fee}
      modern
      error={validationErrors?.fee}
      fractional={6}
    />

    <InputAmount
      label={getTranslate('settlements.registry.rate.header')}
      id="settlements.rate"
      placeholder="0.00"
      value={formData.rate || ''}
      onChange={handlers.rate}
      modern
      error={validationErrors?.rate}
      fractional={4}
    />

    <Input
      id="settlement.transactionHash"
      label={getTranslate('settlements.registry.transactionHash.header')}
      value={formData.transactionHash || ''}
      placeholder={getTranslate('settlements.registry.transactionHash.header')}
      onChange={handlers.transactionHash}
      modern
    />

    <InputDate
      id="settlement.completedDate"
      tooltip={getTranslate('settlements.registry.completedDate.header')}
      selectedDate={formData?.completedDate}
      error={validationErrors?.completedDate}
      title={getTranslate('settlements.registry.completedDate.header')}
      modern
      withCloseButton={false}
      withTime={false}
      onChange={(date) => handlers.completedDate(
        Date.createDate(date, 'date').format('DD.MM.YYYY')
      )}
      isUnselectDate={false}
      scrollIntoView={false}
      closeAfterSelect
    />
  </Fragment>
);